import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Warehouse, DollarSign, TrendingUp, Package, Layers
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => `$${(n || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const AdminCapitalPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/finance/inventory-capital`)
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-4 border-[#467322] border-t-transparent" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-capital-page">
      <div>
        <h1 className="text-4xl font-bold text-[#467322]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Capital de Inventario</h1>
        <p className="text-gray-500 mt-1">Valor del capital invertido en inventario actual</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="capital-kpis">
        <Card className="border-0 shadow-md bg-gradient-to-br from-slate-700 to-slate-900 text-white">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-white/70 text-sm font-medium">Capital Invertido (Costo)</p>
                <p className="text-3xl font-bold mt-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }} data-testid="total-cost-capital">
                  {fmt(data?.total_cost_capital)}
                </p>
                <p className="text-white/50 text-xs mt-1">{data?.total_units || 0} unidades en {data?.total_products || 0} productos</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center">
                <Warehouse size={24} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-blue-100 shadow-md">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium">Valor Total de Venta</p>
                <p className="text-3xl font-bold mt-2 text-blue-600" style={{ fontFamily: 'Barlow Condensed, sans-serif' }} data-testid="total-sale-value">
                  {fmt(data?.total_sale_value)}
                </p>
                <p className="text-gray-400 text-xs mt-1">Si se vende todo el inventario</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center">
                <DollarSign size={24} className="text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-emerald-100 shadow-md">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium">Ganancia Potencial</p>
                <p className="text-3xl font-bold mt-2 text-emerald-600" style={{ fontFamily: 'Barlow Condensed, sans-serif' }} data-testid="potential-profit">
                  {fmt(data?.potential_profit)}
                </p>
                <p className="text-gray-400 text-xs mt-1">
                  Margen: <span className="font-semibold text-emerald-600">{data?.margin_percentage || 0}%</span>
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center">
                <TrendingUp size={24} className="text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Product Breakdown */}
      <Card className="border border-gray-200 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg text-gray-800 flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            <Layers size={18} className="text-[#467322]" />
            Desglose por Producto
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead>SKU</TableHead>
                <TableHead>Producto</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead className="text-center">Cant.</TableHead>
                <TableHead className="text-right">Costo Unit.</TableHead>
                <TableHead className="text-right">Capital Invertido</TableHead>
                <TableHead className="text-right">Valor de Venta</TableHead>
                <TableHead className="text-right">Ganancia Pot.</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!data?.products?.length ? (
                <TableRow><TableCell colSpan={8} className="text-center py-12 text-gray-400">
                  <Package size={36} className="mx-auto mb-2 opacity-40" /><p>No hay productos en inventario</p>
                </TableCell></TableRow>
              ) : (
                <>
                  {data.products.map(p => (
                    <TableRow key={p.sku} className="hover:bg-gray-50" data-testid={`capital-row-${p.sku}`}>
                      <TableCell><Badge variant="outline" className="font-mono text-xs">{p.sku}</Badge></TableCell>
                      <TableCell>
                        <p className="font-semibold text-gray-800">{p.name}</p>
                        {p.brand && <p className="text-xs text-gray-400">{p.brand}</p>}
                      </TableCell>
                      <TableCell>
                        <Badge className={`text-xs ${p.presentation === 'capsula' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-teal-50 text-teal-700 border-teal-200'} border`}>
                          {p.presentation === 'capsula' ? 'Cápsula' : 'Frasco'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className={`${p.quantity > 10 ? 'bg-emerald-100 text-emerald-700' : p.quantity > 0 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                          {p.quantity}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right text-sm">{fmt(p.unit_cost)}</TableCell>
                      <TableCell className="text-right font-medium text-gray-800">{fmt(p.total_cost)}</TableCell>
                      <TableCell className="text-right font-medium text-blue-600">{fmt(p.total_sale_value)}</TableCell>
                      <TableCell className="text-right font-bold text-emerald-600">{fmt(p.potential_profit)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-gray-50 font-bold border-t-2">
                    <TableCell colSpan={3} className="text-right text-gray-600">TOTALES</TableCell>
                    <TableCell className="text-center text-gray-700">{data.total_units}</TableCell>
                    <TableCell />
                    <TableCell className="text-right text-gray-800">{fmt(data.total_cost_capital)}</TableCell>
                    <TableCell className="text-right text-blue-700">{fmt(data.total_sale_value)}</TableCell>
                    <TableCell className="text-right text-emerald-700">{fmt(data.potential_profit)}</TableCell>
                  </TableRow>
                </>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminCapitalPage;
